// src/pages/Leaderboard.jsx — anonymous XP ranking.
// Shows only handle / level / xp — never emails or real names.
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { motion } from "framer-motion";
import { HiChartBar } from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import { weekKey } from "../lib/goals";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };
const medal = (rank) => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null);

export default function Leaderboard() {
  const { user, profile } = useAuth();
  const [rows, setRows] = useState([]);          // all-time, ordered by xp
  const [weekRows, setWeekRows] = useState(null); // this week, ordered by weeklyXp (lazy)
  const [loading, setLoading] = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [scope, setScope] = useState("all"); // "all" | "class" | "week"

  const currentWeek = weekKey();

  // All-time list (single-field orderBy — no composite index).
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "students"), orderBy("xp", "desc"), limit(100)));
        if (!active) return;
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => r.active !== false));
      } catch (e) {
        console.error("Leaderboard load failed", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Weekly list — fetched lazily the first time the user opens that scope.
  useEffect(() => {
    if (scope !== "week" || weekRows !== null) return;
    let active = true;
    (async () => {
      setWeekLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "students"), orderBy("weeklyXp", "desc"), limit(100)));
        if (!active) return;
        setWeekRows(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((r) => r.active !== false && r.weekKey === currentWeek && (r.weeklyXp || 0) > 0)
        );
      } catch (e) {
        console.error("Weekly leaderboard load failed", e);
        setWeekRows([]);
      } finally {
        if (active) setWeekLoading(false);
      }
    })();
    return () => { active = false; };
  }, [scope, weekRows, currentWeek]);

  const isWeek = scope === "week";

  const ranked = useMemo(() => {
    if (isWeek) return (weekRows || []).slice(0, 50);
    let list = rows;
    if (scope === "class" && profile?.board && profile?.grade) {
      list = list.filter((r) => r.board === profile.board && r.grade === profile.grade);
    }
    return list.slice(0, 50);
  }, [isWeek, weekRows, rows, scope, profile]);

  const xpOf = (r) => (isWeek ? r.weeklyXp || 0 : r.xp || 0);

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Leaderboard | Ravi TestPapers" description="See how you rank against other students." canonical="/leaderboard" />
      <div className="max-w-screen-md mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl"><HiChartBar /></div>
          <div>
            <h1 className="text-2xl font-black text-gray-800">Leaderboard</h1>
            <p className="text-sm text-gray-400">
              {isWeek ? "Top students this week — resets every Monday." : "Top students by XP — names stay anonymous."}
            </p>
          </div>
        </div>

        {/* Scope toggle */}
        <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 mb-5">
          <button onClick={() => setScope("all")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${scope === "all" ? "bg-indigo-600 text-white" : "text-gray-500"}`}>
            All time
          </button>
          <button onClick={() => setScope("week")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${scope === "week" ? "bg-indigo-600 text-white" : "text-gray-500"}`}>
            This week
          </button>
          <button onClick={() => setScope("class")} disabled={!profile?.board}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${scope === "class" ? "bg-indigo-600 text-white" : "text-gray-500"} disabled:opacity-40`}>
            My class
          </button>
        </div>

        {isWeek && weekLoading ? (
          <div className="py-16"><Loader /></div>
        ) : ranked.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-400">
            {isWeek ? "No one has earned XP yet this week. Be the first!" : "No students ranked yet."}
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl divide-y divide-gray-50 overflow-hidden">
            {ranked.map((r, i) => {
              const rank = i + 1;
              const isMe = user && r.id === user.uid;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.4) }}
                  className={`flex items-center gap-3 px-4 py-3 ${isMe ? "bg-indigo-50" : ""}`}
                >
                  <div className="w-8 text-center font-black text-gray-400">{medal(rank) || rank}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm truncate ${isMe ? "text-indigo-700" : "text-gray-800"}`}>
                      {r.handle || "Student"} {isMe && <span className="text-xs font-semibold text-indigo-500">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-400">Level {r.level || 1}{r.board ? ` · ${BOARD_LABELS[r.board] || r.board}` : ""}{r.grade ? ` · Class ${r.grade}` : ""}</p>
                  </div>
                  <span className="text-sm font-black text-indigo-600">{xpOf(r)} XP{isWeek ? <span className="text-[10px] font-bold text-gray-400"> /wk</span> : null}</span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
